import { Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import Paragraph from 'components/atoms/Paragraph';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import { usePrevious } from 'hooks/usePrevious';
import useProfily from 'hooks/useProfily';

import cisMenyApi from 'services/cisMeny';
import cisStatyApi from 'services/cisStaty';
import { editaceTextuGet } from 'services/editaceTextu/editaceTextu';
import logoutApi from 'services/logout';
import restoreTokenApi from 'services/restoreToken';

import apiPaths from 'enums/apiPaths';
import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import { routeMapa } from 'data/routeMapa';

import { Context, defaultStoreState } from './Store';
import TranslateText from './TranslateText';

/**
 * Obalovaci komponenta, ktera hlida cas do vyprseni tokenu.
 * Po vyprseni 13min se objevi modal kde je odpocet 1min.
 * Potvrzeni Ok resetuje casovac na 13min,
 * po kliknuti na odhlasit nebo uplynuti 60sec se klient odhlasi.
 */

let ignoreEveryOtherClick = false;

export const Authorization: React.FC = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const {
        token,
        NotificationComponent,
        language,
        setStoreState,
        ucty,
        editaceTextu,
        serviceMode,
        profily,
        cisStaty
    } = useContext(Context);
    const { pathname, search } = useLocation();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [autoLogoutCounter, setAutoLogoutCounter] = React.useState(60);
    const [idleTimeStamp, setIdleTimeStamp] = useState<dayjs.Dayjs>();
    const [tokenTimeStamp, setTokenTimeStamp] = useState<dayjs.Dayjs>();
    const [timeStampCheckInterval, setTimeStampCheckInterval] = useState<number>();
    const prevTokenRef = usePrevious(token);
    const [nonPrivateRoutes] = useState<string[]>(
        routeMapa()
            .filter(e => !e.private && e.path !== routePathEnum.AKCIONAR_SMS && !e.path.includes('redirect-dlt'))
            .map(e => e.path.split('/')[1])
    );
    const isDLTlogin = profily?.typprihlaseni === 'DLT';

    // sledovanie reloadu pri DLT prihlaseni => presmeruje naspat na dlt namiesto odhlasenia
    const redirectToDlt =
        isDLTlogin &&
        window.performance
            .getEntriesByType('navigation')
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((nav: any) => nav.type)
            .includes('reload');
    if (redirectToDlt)
        window.location.replace(
            window.location.origin.includes('styrax')
                ? 'https://dlt-dev.styrax.cz/'
                : window.location.origin.includes('test')
                  ? 'https://test-dlt.cdcp.cz/'
                  : 'https://dlt.cdcp.cz/'
        );

    // zmena stylov a nazvov stranky na zaklade URL
    useEffect(() => {
        window.document.documentElement.lang = language === 'cz' ? 'cs' : 'en';
        const favicon = document.getElementById('favicon') as HTMLFormElement;
        favicon.href =
            window.location.hostname.includes('test') || window.location.hostname.includes('styrax')
                ? 'faviconTest.ico'
                : 'favicon.ico';
        window.location.hostname.includes('test')
            ? (document.title = 'TEST Můj depozitář - Centrální depozitář cenných papírů')
            : null;
    }, []);

    // nastavenie jazyka podla url params ?lang=en
    useEffect(() => {
        let langArr = search
            .substring(1)
            .split('&')
            .filter(e => e.includes('lang'));
        const lang = langArr.length ? langArr[0].split('=')[1] : '';
        window.document.documentElement.lang = language === 'cz' ? 'cs' : 'en';

        if (lang !== language && (lang === 'cz' || lang === 'en')) {
            setLevel('language', lang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const excludedRoutes = [
            routePathEnum.LOGIN_TOKEN,
            routePathEnum.LOGIN_ROZCESTNIK,
            routePathEnum.LOGIN_ROZCESTNIKDLT,
            routePathEnum.EMITENT_LOGIN_MUJDEP,
            routePathEnum.SRD_LOGIN_MUJDEP,
            routePathEnum.LOGIN_JINA_OSOBA,
            routePathEnum.STATNISPRAVA_LOGIN_MUJDEP,
            routePathEnum.LEI_LOGIN_MUJDEP,
            routePathEnum.PXKLIC_LOGIN,
            routePathEnum.LOGOUT,
            routePathEnum.REDIRECT_ERR,
            routePathEnum.REGISTRACE
        ];

        if (excludedRoutes.includes(pathname)) {
            return;
        }

        if (cisStaty.length < 1) {
            cisStatyApi().then(resp => {
                setLevel('cisStaty', resp.data);
            });

            cisMenyApi().then(resp => {
                setLevel('cisMeny', resp.data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // defaultni nastaveni axiosu
    // eslint-disable-next-line no-undef
    window.location.href.includes(routePathEnum.LEI_LOGIN_AKTUALIZACE) &&
        navigateWorkaround(routePathEnum.LEI_LOGIN_MUJDEP); // presmerovanie zo stareho linku
    // eslint-disable-next-line no-undef
    axios.defaults.baseURL = window.location.hostname.includes('localhost')
        ? process.env.REACT_APP_ISB_URL
        : `https://${window.location.hostname}/isbdepositjson/api`;

    useEffect(() => {
        !editaceTextu &&
            serviceMode &&
            ucty?.filter(u => u.typ === EProfilyTyp.TEXTY).length &&
            editaceTextuGet().then(data =>
                setLevel('editaceTextu', {
                    id: data.id,
                    cz: data.cz ? JSON.parse(data.cz) : {},
                    en: data.en ? JSON.parse(data.en) : {}
                })
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceMode, editaceTextu, ucty]);

    /**
     * Interceptor pro kontrolu erroru
     */
    useEffect(() => {
        axios.interceptors.response.use(
            config => {
                return config;
            },
            (error: AxiosError) => {
                const isLeiVarsym = error.config?.url && error.config.url.split('/');
                if (
                    error.response?.status &&
                    error.response.status !== 404 &&
                    error.config?.url &&
                    !(
                        (error.response.status === 400 || error?.response?.status === 409) &&
                        (error.config?.url === apiPaths.NOVY_UCET ||
                            (error.config?.url.includes('KontrolaXML') && error.config?.url.includes('Ucastnik')) ||
                            ((error.config.url.includes('/UE/') || error.config.url.includes('/UED/')) &&
                                error.config.url.includes('/soubor/nacteni')) ||
                            error.config?.url.includes('Pristup/SMS') ||
                            error.config?.url.includes('Portal/Restore') ||
                            error.config?.url.includes('AMLodkaz/prihlasit') ||
                            error.config?.url.includes('AMLodkaz/sms') ||
                            error.config?.url.includes('DOKodkaz/prihlasit') ||
                            error.config?.url.includes('DOKodkaz/sms') ||
                            error.config?.url.includes('LEI/CheckOsobaOR') ||
                            error.config?.url.includes('LEI/Prehled') ||
                            error.config?.url.includes('/zadosti') ||
                            error.config?.url.includes('/EmailOver') ||
                            error.config?.url.includes('/UE/datumupisu') ||
                            error.config?.url.includes('/UE/zalozeni') ||
                            error.config?.url.includes('/UED/zalozeni') ||
                            error.config?.url.includes('/UED/datuhrmax') ||
                            error.config?.url.includes(apiPaths.REGISTRACE))
                    ) &&
                    // nezobrazujem err pri sluzbe LEI/varsymbol (chcekujem ci text za / su cislice)
                    !((isLeiVarsym || []).length === 2 && /^\d+$/.test(isLeiVarsym![1]) && isLeiVarsym![0] === 'LEI') &&
                    !(
                        (error?.response?.status === 401 || error?.response?.status === 409) &&
                        (error.config?.url.includes('LEI/Prihlaseni') ||
                            error.config?.url.includes('ZmenaEmailu/Potvrzeni') ||
                            error.config?.url.includes('LEI/Prevod') ||
                            error.config?.url.includes('Portal/Ucet/') ||
                            error.config?.url.includes('Sluzby/Poslat') ||
                            error.config?.url.includes('AMLodkaz/prihlasit') ||
                            error.config?.url.includes(apiPaths.LOGIN_CERT) ||
                            error.config?.url.includes(apiPaths.LOGIN))
                    )
                ) {
                    console.log(error);
                    NotificationComponent({
                        description: '',
                        title: `RESPONSE_ERROR_${error.response.status.toString()}`,
                        type: ENotificationType.ERROR
                    });
                }
                return Promise.reject(error);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * Funkce na odhlaseni.
     */
    const logOut = useCallback(
        (reroute?: boolean) => {
            logoutApi();
            setShowModal(false);
            setStoreState &&
                setStoreState({
                    ...defaultStoreState,
                    language: language
                });
            window.clearInterval(timeStampCheckInterval);
            setTimeStampCheckInterval(undefined);
            setIdleTimeStamp(undefined);
            setTokenTimeStamp(undefined);
            reroute &&
                setTimeout(
                    () =>
                        isDLTlogin
                            ? window.location.replace(
                                  window.location.origin.includes('styrax')
                                      ? 'https://dlt-dev.styrax.cz/'
                                      : window.location.origin.includes('test')
                                        ? 'https://test-dlt.cdcp.cz/'
                                        : 'https://dlt.cdcp.cz/'
                              )
                            : navigateWorkaround(routePathEnum.LOGOUT),
                    0
                );
            // TODO niekedy sa pri logoute zacykli volanie restore token toto je spinavy fix
            !isDLTlogin && setTimeout(() => window.location.reload(), 500);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [timeStampCheckInterval, language]
    );

    useEffect(() => {
        // pri zmene routy na nonprivate (login, registrace...) odhlasi uzivatela
        token && nonPrivateRoutes.includes(window.location.hash.split('#')[1].split('/')[1]) && logOut();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.hash]);

    // timer vyuzity v modal okne
    useEffect(() => {
        if (autoLogoutCounter === 0) {
            logOut(true);
            setAutoLogoutCounter(60);
        }

        const timer =
            autoLogoutCounter > 0 &&
            showModal &&
            window.setInterval(() => {
                setAutoLogoutCounter(autoLogoutCounter - 1);
            }, 1000);
        return () => {
            if (timer) window.clearInterval(timer);
        };
    }, [autoLogoutCounter, logOut, showModal]);

    /**
     * Podminka ktera nam pri zmene tokenu z null na hodnotu (login)
     * zavola funkci tokenTimer a nastavi casovac.
     */
    if (prevTokenRef === null && token !== null && !tokenTimeStamp && !idleTimeStamp) {
        setIdleTimeStamp(dayjs().add(14, 'minutes'));
        setLevel('logoutTimestamp', dayjs().add(14, 'minutes'));
        setTokenTimeStamp(dayjs().add(14, 'minutes'));
    }

    useEffect(() => {
        if (!token) {
            // vypne casovace pri strate tokenu (napr odhlasenie z logout buttonu)
            window.clearInterval(timeStampCheckInterval);
            setTimeStampCheckInterval(undefined);
            setIdleTimeStamp(undefined);
            setTokenTimeStamp(undefined);
            return;
        }
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        setTokenTimeStamp(dayjs().add(14, 'minutes'));
        !idleTimeStamp && setIdleTimeStamp(dayjs().add(14, 'minutes'));
        !idleTimeStamp && setLevel('logoutTimestamp', dayjs().add(14, 'minutes'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if ((idleTimeStamp || tokenTimeStamp) && token) {
            setTimeStampCheckInterval(pv => {
                window.clearInterval(pv);
                return window.setInterval(() => {
                    // Funkce ktera zapina casovac pro token.
                    if (idleTimeStamp && idleTimeStamp < dayjs()) {
                        setShowModal(true);
                    }
                    // Funkce ktera sleduje aktivitu uzivatela a otvori okno pre predlzenie platnosti
                    if (tokenTimeStamp && tokenTimeStamp < dayjs()) {
                        restoreTokenApi()
                            .then(({ data }) => {
                                setLevel('token', data.token);
                            })
                            .catch(() => logOut(true));
                    }
                }, 10000);
            });
        }
        return () => {
            window.clearInterval(timeStampCheckInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idleTimeStamp, tokenTimeStamp]);

    /**
     * Zavola restoreTokenApi a po dokonceni Promisu zavre modal.
     */
    const stayLogged = () => {
        setLoading(val => !val);
        restoreTokenApi()
            .then(({ data }) => {
                setLevel('token', data.token);
                setAutoLogoutCounter(60);
            })
            .catch((err: any) =>
                NotificationComponent({
                    title: 'CHYBA_RESTORE_TOKEN',
                    description: err.message
                })
            )
            .finally(() => {
                setShowModal(false);
                setLoading(val => !val);
            });
    };
    return (
        <div
            onClick={() => {
                if (ignoreEveryOtherClick) {
                    ignoreEveryOtherClick = false;
                    token && setIdleTimeStamp(dayjs().add(14, 'minutes'));
                    token && setLevel('logoutTimestamp', dayjs().add(14, 'minutes'));
                } else {
                    ignoreEveryOtherClick = true;
                }
            }}
        >
            <Outlet />
            <Modal
                open={showModal}
                onOk={stayLogged}
                onCancel={() => logOut(true)}
                confirmLoading={loading}
                closable={false}
                maskClosable={false}
                zIndex={1001}
                cancelText={<TranslateText>ODHLASIT_SE</TranslateText>}
            >
                <Paragraph text="EMITENTSKA_AUTOMATICKE_ODHLASENI" />
                <Row>
                    <TranslateText params={[autoLogoutCounter.toString()]}>TIMER_LOGOUT</TranslateText>
                </Row>
            </Modal>
        </div>
    );
};
